<template>
  <div :class="bannerClasses">
    <div v-if="isLockedByYou" class="lock-checkpoint-banner__accent" />

    <div class="lock-checkpoint-banner__icon-wrap">
      <Icon v-if="isLockedByYou || isLockedByOther" name="lock_checkpoint_icon" />
      <svg
        v-else-if="isOtherSteppedAway"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        width="20"
        height="20"
        aria-hidden="true"
      >
        <path
          d="M12 6V12L16 14M22 12C22 17.52 17.52 22 12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12 2C17.52 2 22 6.48 22 12Z"
          stroke="#92400e"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <div class="lock-checkpoint-banner__content">
      <p class="lock-checkpoint-banner__title" :class="titleClass">
        <template v-if="isVideoProcessingLock">
          This checkpoint is now locked by you until you process the video.
        </template>
        <template v-else-if="isLockedByYou">This checkpoint is now locked by you.</template>
        <template v-else-if="isLockedByOther">{{ editorName }} is editing this draft.</template>
        <template v-else-if="isOtherSteppedAway">
          This checkpoint is locked by {{ editorName }} but they may have stepped away.
        </template>
      </p>

      <p v-if="isVideoProcessingLock" class="lock-checkpoint-banner__supporting">
        Cancelling will discard the video and delete this draft checkpoint.
      </p>

      <p v-else-if="isLockedByYou" class="lock-checkpoint-banner__supporting">
        As long as you are editing, nobody else can edit this checkpoint
      </p>

      <p
        v-if="isOtherSteppedAway"
        class="lock-checkpoint-banner__supporting lock-checkpoint-banner__supporting--amber"
      >
        You can take over editing if you need to act now.
      </p>
    </div>

    <StandardButton
      v-if="isVideoProcessingLock"
      type="magenta"
      variant="primary"
      size="small"
      label="Cancel and Discard"
      @click="$emit('cancel-and-discard')"
    />

    <StandardButton
      v-else-if="isOtherSteppedAway"
      type="magenta"
      variant="primary"
      size="small"
      label="Take over editing"
      @click="$emit('takeover')"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const props = defineProps({
  locked: {
    type: String,
    default: "by-you",
    validator: (val) => ["by-you", "by-other", "other-stepped-away"].includes(val),
  },
  editorName: {
    type: String,
    default: "",
  },
  /** When true with locked=by-you: video-encoding lock copy + Cancel and Discard. */
  videoProcessing: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["takeover", "cancel-and-discard"]);

const bannerClasses = computed(() => [
  "lock-checkpoint-banner",
  `lock-checkpoint-banner--${props.locked}`,
  {
    "lock-checkpoint-banner--video-processing": props.locked === "by-you" && props.videoProcessing,
  },
]);

const isLockedByYou = computed(() => props.locked === "by-you");
const isLockedByOther = computed(() => props.locked === "by-other");
const isOtherSteppedAway = computed(() => props.locked === "other-stepped-away");
const isVideoProcessingLock = computed(() => isLockedByYou.value && props.videoProcessing);

const titleClass = computed(() => ({
  "lock-checkpoint-banner__title--amber": isOtherSteppedAway.value,
}));
</script>

<style scoped>
.lock-checkpoint-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  width: 100%;
}

.lock-checkpoint-banner--by-you {
  background-color: #f2f4f7;
}

.lock-checkpoint-banner--by-other {
  background-color: #f2f4f7;
}

.lock-checkpoint-banner--other-stepped-away {
  background-color: #fffbeb;
  border: 1px solid #e58700;
}

.lock-checkpoint-banner__accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: var(--primary, #7f56d9);
}

.lock-checkpoint-banner--by-you .lock-checkpoint-banner__icon-wrap {
  margin-left: 6px;
}

.lock-checkpoint-banner__icon-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lock-checkpoint-banner__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lock-checkpoint-banner__title {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  margin: 0;
}

.lock-checkpoint-banner__title--amber {
  color: #92400e;
}

.lock-checkpoint-banner__supporting {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: #475467;
  margin: 0;
}

.lock-checkpoint-banner__supporting--amber {
  color: #b45309;
}
</style>
